import { BUILDINGS } from '../../data/buildings.js';
import { demoteVisibleToExplored, stampVision } from '../../fog/FogGrid.js';
import type { MatchState } from '../State.js';
import { isOperational } from '../entities/Building.js';
import type { PlayerId } from '../ids.js';

// Vision radii (in tiles) for entities whose static defs don't carry one.
// Numbers come from docs/02_GAME_DESIGN.md §5 "Vision sources":
//   Core 10, ControlNode (held relay) 8.
// Squad and Building vision come from their respective data defs (SquadDef.vision,
// BuildingDef.vision). Tile-based so they line up cleanly with the fog grid.
//
// Core vision is sized so any enemy in attack range of the Core is revealed
// even when the defender has no surviving squads. Max squad attack range is
// 280 px (Recon) ≈ 8.75 tiles, so 10 tiles (320 px) leaves a small buffer
// — the player can always see who is shooting their Core.
export const CORE_VISION_TILES = 10;
export const HELD_RELAY_VISION_TILES = 8;
// Mark Target (Recon Q): the marked enemy is revealed through fog to the marker
// for `durationSec`. Implemented as a small vision pulse at the target's position
// in the marker's fog grid, just large enough to stamp the cell visible.
const MARK_VISION_TILES = 2;

// Per-tick fog update. For each player p:
//   1. Demote every cell that was 'visible' last tick to 'explored'.
//   2. Re-stamp 'visible' on cells covered by p's vision sources this tick.
//      Sources: owned cores, owned operational buildings, owned squads, and held
//      (owned) control nodes.
//
// Order: this system runs after movement/combat/capture/economy so positions and
// ownership are settled before stamping. Squads that died this tick are gone from
// the squads table by the time we read it; their final position contributes only
// to the previous tick's fog. (This matches "no ghost units" in §5.)
export function fogSystem(state: MatchState): void {
  for (const p of [0, 1] as const) {
    const grid = state.fog[p];
    demoteVisibleToExplored(grid);
  }
  for (const core of Object.values(state.cores)) {
    const grid = state.fog[core.ownerId];
    stampVision(grid, core.pos.x, core.pos.y, CORE_VISION_TILES);
  }
  for (const b of Object.values(state.buildings)) {
    if (!isOperational(b)) continue;
    // V1.5+ forward turret network dependency. Offline mini-turrets
    // contribute no vision — losing the network means the forward position
    // goes blind. Same "felt on the battlefield" rule the §16 isolated-relay
    // vision pulse already applies, extended to forward turrets.
    if (!b.isOnline) continue;
    const def = BUILDINGS[b.kind];
    const grid = state.fog[b.ownerId];
    stampVision(grid, b.pos.x, b.pos.y, def.vision);
  }
  for (const sq of Object.values(state.squads)) {
    const grid = state.fog[sq.ownerId];
    stampVision(grid, sq.pos.x, sq.pos.y, sq.vision);
  }
  for (const node of Object.values(state.controlNodes)) {
    if (node.ownerId === null) continue;
    // V1.5 Option A: an isolated relay grants no vision. SignalLinkSystem
    // sets isConnected=false on owned-but-disconnected relays. With signal
    // links disabled (V0.x), the flag is always true so vision is unchanged.
    if (!node.isConnected) continue;
    // V1.5+ Slice 5 — Jam Relay: a jammed relay grants no vision to its
    // owner for the jam window. Distinct from cut/isolated: the chain stays
    // intact (BFS still traverses through; income unaffected; downstream
    // relays stay connected) — only the jammed node's vision pulse goes
    // dark. This is the surgical "blind spot" disruption per
    // docs/02_GAME_DESIGN.md §16.
    if (state.tick < node.jammedUntilTick) continue;
    const grid = state.fog[node.ownerId as PlayerId];
    stampVision(grid, node.pos.x, node.pos.y, HELD_RELAY_VISION_TILES);
  }
  // Mark Target reveals the marked enemy through fog to the marking player.
  // Stamp a small vision footprint at the target's current position in the
  // marker's grid; the renderer uses fog visibility as its show-enemy gate so
  // a stamped cell is enough.
  for (const sq of Object.values(state.squads)) {
    if (sq.markedByPlayerId === null) continue;
    if (state.tick >= sq.markedUntilTick) continue;
    const grid = state.fog[sq.markedByPlayerId];
    stampVision(grid, sq.pos.x, sq.pos.y, MARK_VISION_TILES);
  }
}
